import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Link2, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Pantalla intermedia posterior al login con Google cuando el email
 * verificado por Google NO existe en `usuarios`. Le pedimos matrícula +
 * DNI al matriculade para validar contra `trabajadores_sociales` y crear
 * la cuenta vinculada.
 */
export default function VincularCuentaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const googleEmail = state.googleEmail || '';
  const googleName = state.googleName || '';
  const googlePicture = state.googlePicture || '';

  const [matricula, setMatricula] = useState('');
  const [dni, setDni] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Si alguien aterriza acá sin haber pasado por Google, mandamos al login.
  if (!googleEmail) {
    return <Navigate to="/login" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!matricula.trim() || !dni.trim()) {
      setError('Ingresá matrícula y DNI.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/google/link`, {
        google_email: googleEmail,
        google_name: googleName,
        matricula: matricula.trim(),
        dni: dni.trim(),
      });
      const { access_token, user } = res.data || {};
      if (!access_token) throw new Error('Sin token');
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      window.location.replace('/');
    } catch (err) {
      console.error('[VincularCuenta] Error:', err);
      setError(err?.response?.data?.detail || 'No pudimos vincular tu cuenta. Verificá los datos.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <Card className="w-full max-w-md" data-testid="page-vincular-cuenta">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Link2 className="w-5 h-5" />
            </div>
            <div>
              <CardTitle>Vincular cuenta con tu legajo institucional</CardTitle>
              <CardDescription>
                Es la primera vez que ingresás con Google. Necesitamos validar tu matrícula.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="flex items-center gap-3 bg-gray-50 border rounded-md p-3 mb-5">
            {googlePicture ? (
              <img
                src={googlePicture}
                alt={googleName}
                className="w-10 h-10 rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center font-semibold">
                {(googleName || googleEmail).slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="text-sm">
              <p className="font-medium text-gray-900" data-testid="vincular-nombre-google">{googleName || 'Cuenta de Google'}</p>
              <p className="text-gray-600" data-testid="vincular-email-google">{googleEmail}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="matricula-vincular">Número de matrícula</Label>
              <Input
                id="matricula-vincular"
                data-testid="input-matricula-vincular"
                autoFocus
                value={matricula}
                onChange={(e) => setMatricula(e.target.value)}
                placeholder="Ej: 1234"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="dni-vincular">DNI</Label>
              <Input
                id="dni-vincular"
                data-testid="input-dni-vincular"
                value={dni}
                onChange={(e) => setDni(e.target.value)}
                placeholder="Sólo números"
                inputMode="numeric"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Ingresá el DNI sin puntos ni guiones. Lo comparamos con tu legajo profesional.
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm" data-testid="vincular-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={loading}
                data-testid="btn-vincular-cuenta"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando…</>
                ) : (
                  'Vincular y entrar'
                )}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                data-testid="btn-cancelar-vincular"
              >
                Cancelar y volver
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
